// The piano CHORD-MELODY engine (Aug 2026) — the keyboard counterpart to the
// guitar's melody-led arrangement, and the piece that completes the Chords
// menu: with it, the melody lens can draw and play keyboards instead of
// falling back to fretboards.
//
// The texture is the classic solo-piano reading: the WRITTEN MELODY NOTE on
// top, exact to the octave — never a pitch-class stand-in — with the chord's
// support tones packed under it inside the right hand and the root stated by
// the left. Candidates vary in how much sits under the melody (the full
// support set, guide-tone pairs, a single tone, or the bare melody-plus-root)
// and in which tone sits directly beneath it, so the voice-leading walk has
// real choices while the top voice never moves.
//
// What is deliberately REUSED rather than reinvented: the chord's realization
// around the melody is the guitar plan's (`arrangementSequence` upgrades the
// type when the melody is a tension and applies the sus convention — this
// module just voices what it is handed); the gates are the piano's own
// (`hasFlatNineClashMidis` + `chordOwnsFlatNine`, `isMuddy`); the metric is
// hand-aware phrase search (`pianoVoiceLeading`); spelling is `spellVoicingMidis`.
// And the guitar's melody-wins rule carries over whole: when the avoid-b9
// filter would reject every candidate that tops the melody, the flagged
// candidates are re-admitted — a sounded wrong note is worse than an
// internal minor ninth.

import { CHORD_TYPES, chordOwnsFlatNine, type ChordTypeId } from './chords';
import { spellVoicingMidis } from './chordSpelling';
import { pitchClassOf, sharpName } from './notes';
import {
  candidatesForCompChord,
  pinnedInstrumentVoicing,
  type PianoCompOptions,
  type PinnedPianoVoicing,
} from './pianoComping';
import { hasFlatNineClashMidis, isMuddy } from './pianoVoicings';
import { pianoMovement, pianoPhrase } from './pianoVoiceLeading';

const mod12 = (n: number) => ((n % 12) + 12) % 12;

/** The left hand's band for the stated root — E2..C4, the two-hand family's
 * own register, low enough to ground the texture without leaving the bench. */
const LH_LOW = 40;
const LH_HIGH = 60;
/** How far the left hand may drop when no stack fits its band: the piano's
 * lowest key, A0. */
const LH_FLOOR = 21;
/** One hand's reach for the melody-topped right-hand stack. */
const DEFAULT_RH_SPAN = 14;
/** Where the opening chord's bass prefers to sit (C3, like the comp walk). */
const DEFAULT_ANCHOR = 48;

export interface PianoMelodyChord {
  root: string;
  /** The slash bass as written ("C" in "D/C") — what the left hand states. */
  bass?: string;
  chordType: ChordTypeId;
  /** The root AS WRITTEN — spelling follows it (an E♭7 reads D♭, never C♯). */
  displayRoot?: string;
  /** The melody note this chord must carry on top, exact to the octave.
   * Absent (or null, the guitar optimiser's spelling of absent) = a
   * chart-only span inside the tune: the walk fills it with the ordinary
   * two-hand comping candidates, like the guitar plan's free top. */
  targetTopMidi?: number | null;
  /** The user's own stack for this chord (voicingPins.ts, at the current
   * root): the whole candidate layer, as in the comping walk. */
  pinnedVoicing?: PinnedPianoVoicing;
}

export interface PianoMelodyVoicing {
  /** Sounding pitches, low→high; the last one IS the melody note. */
  midis: number[];
  /** Diatonic note names for `midis`, low→high. */
  voicing: string[];
  hands: { left: number[]; right: number[] };
}

export type PianoMelodyVoicedChord<T> = T & {
  midis: number[];
  voicing: string[];
  hands?: { left: number[]; right: number[] };
  /** Pitch-space movement from the previous chord (undefined for the first). */
  distance?: number;
};

export interface PianoMelodyOptions {
  /** The right hand's reach under the melody note (default 14, a major 9th). */
  maxHandSpan?: number;
  avoidB9?: boolean;
  /** Where the opening chord's bass should sit (default C3). */
  anchorMidi?: number;
}

/** The support pitch classes for a melody-topped voicing: the chord's voiced
 * tones minus the melody's own class — and when the melody is NOT a chord
 * tone, minus one displaced tone so the stack stays four voices under one
 * hand: a natural 11 over a major 3rd displaces the 3rd (the sus convention,
 * the same rule the guitar plan applies), anything else gives up the 5th
 * first, then the root-slot tone. */
export function melodySupportPcs(root: string, chordType: ChordTypeId, topMidi: number): number[] {
  const rootPc = pitchClassOf(root);
  const melodyIv = mod12(topMidi - rootPc);
  const intervals = CHORD_TYPES[chordType].intervals.map(mod12);
  if (intervals.includes(melodyIv)) {
    return intervals.filter((iv) => iv !== melodyIv).map((iv) => mod12(rootPc + iv));
  }
  const kept = [...intervals];
  const drop = (predicate: (iv: number) => boolean) => {
    const at = kept.findIndex(predicate);
    if (at >= 0) kept.splice(at, 1);
    return at >= 0;
  };
  if (melodyIv === 5 && intervals.includes(4)) drop((iv) => iv === 4);
  else if (!drop((iv) => iv === 7)) drop((iv) => iv === 0);
  return kept.map((iv) => mod12(rootPc + iv));
}

/** Place each pitch class at its nearest octave strictly below the voice
 * above it — the downward analogue of the enumerators' ascending stack. */
function stackDownward(top: number, pcs: number[]): number[] {
  const out: number[] = [];
  let above = top;
  for (const pc of pcs) {
    const step = mod12(above - pc) || 12;
    const midi = above - step;
    out.push(midi);
    above = midi;
  }
  return out.reverse();
}

function permutations<T>(items: T[]): T[][] {
  if (items.length <= 1) return [items];
  return items.flatMap((item, i) =>
    permutations([...items.slice(0, i), ...items.slice(i + 1)]).map((rest) => [item, ...rest]),
  );
}

/**
 * Candidates for one melody-topped chord. Every candidate's top note is
 * `targetTopMidi` exactly; they differ in what the right hand packs beneath
 * it and where the left hand states the root. The left hand keeps to its
 * E2–C4 band; where nothing fits there (a melody around C3–E3, where an 8vb
 * shift puts many a tune) it drops to the nearest octave below each stack
 * that reads, down to the keyboard's bottom A. An octave or more under the
 * melody no interval limit applies, so the bare melody-plus-root pair always
 * voices a melody from A♭2 up, which is what makes the walk total.
 */
export function enumerateChordMelodyPiano(
  chord: PianoMelodyChord & { targetTopMidi: number },
  options: PianoMelodyOptions = {},
): PianoMelodyVoicing[] {
  const top = chord.targetTopMidi;
  const rhSpan = options.maxHandSpan ?? DEFAULT_RH_SPAN;
  const avoidB9 = options.avoidB9 ?? true;
  const rootPc = pitchClassOf(chord.root);
  const support = melodySupportPcs(chord.root, chord.chordType, top);

  // Right-hand fillings: every ordering of every subset of the support tones
  // (K ≤ 3, so this is small), stacked downward from the melody; the empty
  // subset is the bare melody. Orderings matter because the first tone sits
  // directly under the melody — that choice is most of a voicing's colour.
  const fillings = new Map<string, number[]>();
  const subsets: number[][] = [[]];
  for (const pc of support) subsets.push(...subsets.map((subset) => [...subset, pc]));
  for (const subset of subsets) {
    for (const order of permutations(subset)) {
      const under = stackDownward(top, order);
      const rh = [...under, top];
      if (top - rh[0] > rhSpan) continue;
      fillings.set(rh.join(','), rh);
    }
  }

  // The left hand states the chord's bottom note — its root, or the slash
  // bass where the symbol names one — at every octave of its band that stays
  // below the right hand; the walk picks among them by voice leading.
  const lhPc = chord.bass ? pitchClassOf(chord.bass) : rootPc;
  const out = new Map<string, PianoMelodyVoicing>();
  const place = (lh: number, rh: number[]): boolean => {
    const midis = [lh, ...rh];
    if (isMuddy(midis)) return false;
    out.set(midis.join(','), {
      midis,
      voicing: spellVoicingMidis(
        midis,
        chord.displayRoot ?? chord.root,
        chord.chordType,
        sharpName,
      ),
      hands: { left: [lh], right: rh },
    });
    return true;
  };
  for (const rh of fillings.values()) {
    for (let lh = LH_LOW + mod12(lhPc - LH_LOW); lh <= LH_HIGH; lh += 12) {
      if (lh >= rh[0]) break;
      place(lh, rh);
    }
  }
  // Too low for the band: the nearest octave below each stack that reads.
  if (!out.size) {
    for (const rh of fillings.values()) {
      for (let lh = rh[0] - (mod12(rh[0] - lhPc) || 12); lh >= LH_FLOOR; lh -= 12) {
        if (place(lh, rh)) break;
      }
    }
  }

  const all = [...out.values()];
  if (!avoidB9 || chordOwnsFlatNine(chord.chordType)) return all;
  const clean = all.filter((candidate) => !hasFlatNineClashMidis(candidate.midis));
  // The guitar's melody-wins rule: if the b9 filter would silence the melody
  // note entirely, the flagged candidates come back.
  return clean.length ? clean : all;
}

/** A pinned stack with no hand split — the inspector's Place notes keyboard
 * records only the keys — read the way this walk voices everything else: the
 * lowest note is the left hand's when it sits in the left hand's band, the
 * rest is the right hand's. Left as `{ left: [] }` the pin charged the bass as
 * an unpaired right-hand voice, and its missing left hand made the walk's
 * register cost NaN, which poisoned every later chord of the tune. */
function inferredHands(midis: number[]): { left: number[]; right: number[] } {
  return midis.length > 1 && midis[0] <= LH_HIGH
    ? { left: [midis[0]], right: midis.slice(1) }
    : { left: [], right: midis };
}

/** What dropping a support tone must SAVE in hand movement to be worth it.
 * The sparse candidates exist for tight spots (a low melody the mud gate
 * boxes in), not as a default: without this the walk opened on the bare
 * melody-plus-root pair and the metric's unpaired-voice charge then made
 * growing back MORE expensive than staying thin — the whole corpus rendered
 * as two-note diagrams (found on the first screenshot, not by the sweeps,
 * which only checked the top note). */
const THIN_WEIGHT = 6;

/**
 * The chord-melody walk: phrase optimization with every melody-carrying
 * chord's top pinned — so the search only ever chooses how the INSIDE voices
 * and the left hand move, and how full the texture is (fullest by default,
 * see THIN_WEIGHT). Chords without a melody note take the ordinary two-hand
 * comping candidates, exactly as the guitar plan frees the top where nothing
 * is written. Null for an empty sequence or an unvoiceable supplied constraint.
 */
export function voiceLeadPianoMelody<T extends PianoMelodyChord>(
  sequence: T[],
  options: PianoMelodyOptions = {},
): PianoMelodyVoicedChord<T>[] | null {
  if (sequence.length === 0) return null;
  const anchor = options.anchorMidi ?? DEFAULT_ANCHOR;
  const compOptions: PianoCompOptions = {
    family: 'two-hand',
    avoidB9: options.avoidB9,
  };

  const candidatesOf = (chord: T): PianoMelodyVoicing[] => {
    if (chord.pinnedVoicing) {
      const pinned = pinnedInstrumentVoicing(chord, 'two-hand');
      return [
        {
          midis: pinned.midis,
          voicing: pinned.voicing,
          hands: pinned.hands ?? inferredHands(pinned.midis),
        },
      ];
    }
    if (chord.targetTopMidi != null) {
      return enumerateChordMelodyPiano(chord as T & { targetTopMidi: number }, options);
    }
    return candidatesForCompChord(chord, compOptions).map((candidate) => ({
      midis: candidate.midis,
      voicing: candidate.voicing,
      hands: candidate.hands ?? {
        left: [],
        right: candidate.midis,
      },
    }));
  };

  const layers = sequence.map(candidatesOf);
  const fullest = layers.map((candidates) => Math.max(...candidates.map((c) => c.midis.length)));
  const chosen = pianoPhrase(
    layers,
    // A candidate with no left hand (a pin placed wholly above the left
    // hand's band, or a slash chord's close stack the bassist completes) is
    // placed by its bottom note — never `undefined - anchor`, whose NaN loses
    // every comparison in the phrase search from that chord on.
    (candidate, index) =>
      THIN_WEIGHT * (fullest[index] - candidate.midis.length) +
      Math.abs((candidate.hands.left[0] ?? candidate.midis[0]) - anchor) * (index === 0 ? 1 : 0.1),
    (a, b) => pianoMovement(a, b, true),
  );
  if (!chosen) return null;
  return chosen.map((candidate, index) => ({
    ...sequence[index],
    midis: candidate.midis,
    voicing: candidate.voicing,
    hands: candidate.hands,
    ...(index ? { distance: pianoMovement(chosen[index - 1], candidate) } : {}),
  }));
}
